import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { Note, NoteType, Site, User } from '@shared/types'
import { get, post } from '../../lib/api'
import { pluralise } from '../../lib/format'
import { useSession } from '../../state/session'
import { useToast } from '../../state/toast'
import { NoteCard } from '../../components/NoteCard'
import { Button, EmptyState, Field, Sheet, Spinner } from '../../components/ui'

type AckFilter = 'pending' | 'all' | 'done'

/**
 * Short by design. Three buttons share one row on a phone, and at large system
 * text sizes a long label is what pushes the page sideways.
 */
const FILTER_LABELS: Record<AckFilter, string> = {
  pending: 'To review',
  all: 'All',
  done: 'Done',
}

/**
 * The review queue.
 *
 * Change requests, issues and safety notes are the ones that cost money if
 * they sit unread, so "Needs attention" is the default view and acknowledging
 * is a single tap — with a bulk action for the morning backlog.
 */
export default function AdminNotes() {
  const { settings } = useSession()
  const toast = useToast()
  const [params, setParams] = useSearchParams()

  const [notes, setNotes] = useState<Note[] | null>(null)
  const [noteTypes, setNoteTypes] = useState<NoteType[]>([])
  const [sites, setSites] = useState<Site[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [ackTarget, setAckTarget] = useState<Note | 'bulk' | null>(null)
  const [ackComment, setAckComment] = useState('')

  const filter = (params.get('filter') as AckFilter) ?? 'pending'
  const siteId = params.get('siteId') ?? ''
  const userId = params.get('userId') ?? ''
  const type = params.get('type') ?? ''

  const buildQuery = useCallback((next?: string | null) => {
    const query = new URLSearchParams({ limit: '30' })
    if (filter === 'pending') query.set('needsAck', 'true')
    if (filter === 'done') query.set('ack', 'done')
    if (siteId) query.set('siteId', siteId)
    if (userId) query.set('userId', userId)
    if (type) query.set('type', type)
    if (next) query.set('cursor', next)
    return query
  }, [filter, siteId, userId, type])

  const load = useCallback(async () => {
    setNotes(null)
    setSelected(new Set())
    const res = await get<{ notes: Note[]; nextCursor: string | null }>(
      `/api/notes?${buildQuery()}`,
    )
    setNotes(res.notes)
    setCursor(res.nextCursor)
  }, [buildQuery])

  useEffect(() => {
    void Promise.all([
      get<{ noteTypes: NoteType[] }>('/api/admin/note-types'),
      get<{ sites: Site[] }>('/api/admin/sites'),
      get<{ users: User[] }>('/api/admin/users'),
    ]).then(([t, s, u]) => {
      setNoteTypes(t.noteTypes)
      setSites(s.sites)
      setUsers(u.users)
    }).catch(() => undefined)
  }, [])

  useEffect(() => {
    void load().catch(() => setNotes([]))
  }, [load])

  const setParam = (key: string, value: string) => {
    if (value) params.set(key, value)
    else params.delete(key)
    setParams(params, { replace: true })
  }

  const loadMore = async () => {
    if (!cursor) return
    setLoadingMore(true)
    try {
      const res = await get<{ notes: Note[]; nextCursor: string | null }>(
        `/api/notes?${buildQuery(cursor)}`,
      )
      setNotes((current) => [...(current ?? []), ...res.notes])
      setCursor(res.nextCursor)
    } finally {
      setLoadingMore(false)
    }
  }

  const acknowledge = async () => {
    const comment = ackComment.trim() || undefined
    try {
      if (ackTarget === 'bulk') {
        await post('/api/notes/acknowledge-bulk', { ids: [...selected], comment })
        toast.success(`Acknowledged ${pluralise(selected.size, 'update')}`)
      } else if (ackTarget) {
        await post(`/api/notes/${ackTarget.id}/acknowledge`, { comment })
        toast.success('Acknowledged')
      }
      setAckTarget(null)
      setAckComment('')
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not acknowledge')
    }
  }

  const toggleSelect = (note: Note, isSelected: boolean) => {
    setSelected((current) => {
      const next = new Set(current)
      if (isSelected) next.add(note.id)
      else next.delete(note.id)
      return next
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-1">
        {(['pending', 'all', 'done'] as AckFilter[]).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setParam('filter', value)}
            className={`btn flex-1 px-2 text-xs ${filter === value ? 'btn-primary' : 'btn-secondary'}`}
          >
            {FILTER_LABELS[value]}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <select className="field" value={siteId} onChange={(e) => setParam('siteId', e.target.value)}>
          <option value="">All sites</option>
          {sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
        </select>
        <select className="field" value={userId} onChange={(e) => setParam('userId', e.target.value)}>
          <option value="">Everyone</option>
          {users.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
        </select>
        <select className="field" value={type} onChange={(e) => setParam('type', e.target.value)}>
          <option value="">All types</option>
          {noteTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>

      {selected.size > 0 && (
        <div className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-2 bg-brand px-3 py-2 text-ink">
          <span className="text-sm font-bold">{pluralise(selected.size, 'update')} selected</span>
          <div className="flex gap-2">
            <Button className="min-h-9 px-3 text-xs" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
            <Button
              variant="primary"
              className="min-h-9 px-3 text-xs"
              onClick={() => setAckTarget('bulk')}
            >
              Acknowledge all
            </Button>
          </div>
        </div>
      )}

      {!notes ? (
        <div className="py-16"><Spinner /></div>
      ) : notes.length === 0 ? (
        <EmptyState
          title={filter === 'pending' ? 'Queue is clear' : 'Nothing here'}
          body={
            filter === 'pending'
              ? 'Every change request, issue and safety note has been acknowledged.'
              : 'No updates match these filters.'
          }
        />
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              noteTypes={noteTypes}
              timezone={settings.timezone}
              showAuthor
              selected={selected.has(note.id)}
              onSelect={note.acknowledgedAt ? undefined : toggleSelect}
              onAcknowledge={setAckTarget}
              sites={sites}
              onSiteChanged={(updated) =>
                setNotes((current) => current?.map((n) => (n.id === updated.id ? updated : n)) ?? null)}
            />
          ))}
          {cursor && (
            <Button className="w-full" loading={loadingMore} onClick={() => void loadMore()}>
              Load older
            </Button>
          )}
        </div>
      )}

      <Sheet
        open={ackTarget !== null}
        onClose={() => {
          setAckTarget(null)
          setAckComment('')
        }}
        title="Acknowledge"
        footer={
          <Button variant="primary" size="lg" className="w-full" onClick={() => void acknowledge()}>
            {ackTarget === 'bulk'
              ? `Acknowledge ${pluralise(selected.size, 'update')}`
              : 'Acknowledge'}
          </Button>
        }
      >
        <p className="mb-4 text-sm text-graphite-2">
          The crew member sees that this was read, along with anything you write here.
        </p>
        <Field label="Reply" hint="Optional">
          <textarea
            className="field"
            value={ackComment}
            onChange={(e) => setAckComment(e.target.value)}
            placeholder="Ordered the windows, delivery Thursday."
          />
        </Field>
      </Sheet>
    </div>
  )
}
